import { FunctionComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { route, RoutableProps } from 'preact-router';
import QRCode from 'qrcode-svg';
import { Layout } from '../components/layout';
import { initiateMfaSetup, verifyMfaSetup, MfaInitiateResponse } from '../services/mfa.service';

type Phase = 'loading' | 'ready' | 'verifying' | 'failed';

export const MfaSetupPage: FunctionComponent<RoutableProps> = () => {
  const [phase, setPhase] = useState<Phase>('loading');
  const [setup, setSetup] = useState<MfaInitiateResponse | null>(null);
  const [qrSvg, setQrSvg] = useState<string>('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    initiateMfaSetup()
      .then((response) => {
        setSetup(response);
        setQrSvg(new QRCode({content: response.otpauthUrl, padding: 0, width: 240, height: 240, ecl: 'M'}).svg());
        setPhase('ready');
      })
      .catch(() => {
        setError('MFA-Setup konnte nicht gestartet werden. Bitte ab- und neu anmelden.');
        setPhase('failed');
      });
  }, []);

  const submit = async (e: Event) => {
    e.preventDefault();
    if (!setup || phase === 'verifying') return;

    setError(null);
    setPhase('verifying');

    try {
      await verifyMfaSetup(code);
      route('/mfa/setup/done', true);
    } catch {
      setError('Code ist ungültig. Bitte erneut versuchen.');
      setCode('');
      setPhase('ready');
    }
  };

  if (phase === 'loading') {
    return (
      <Layout title="Zwei-Faktor einrichten">
        <div class="center mt-lg muted">QR-Code wird vorbereitet …</div>
      </Layout>
    );
  }

  if (phase === 'failed') {
    return (
      <Layout title="Fehler" subtitle="Setup konnte nicht gestartet werden.">
        {error && <div class="error mt-md">{error}</div>}
        <button class="btn mt-md" onClick={() => route('/logout')}>
          Abmelden
        </button>
      </Layout>
    );
  }

  return (
    <Layout title="Zwei-Faktor einrichten" subtitle="Pflicht für die TKW-App." showLogout>
      <ol class="muted mt-md" style="padding-left: 20px; line-height: 1.6;">
        <li>Öffne deine Authenticator-App (z.B. Google Authenticator, Microsoft Authenticator).</li>
        <li>Scanne den QR-Code unten.</li>
        <li>Gib den 6-stelligen Code aus der App ein.</li>
      </ol>

      <div class="qr-wrap" dangerouslySetInnerHTML={{__html: qrSvg}} />

      <details class="muted">
        <summary>Code manuell eingeben</summary>
        <code style="display:block; word-break:break-all; padding:8px; background:var(--input-bg); border-radius:6px; margin-top:6px; font-family:'SF Mono', monospace; font-size:0.85rem;">
          {setup?.secretBase32}
        </code>
      </details>

      <form onSubmit={submit} class="mt-md">
        <div class="input-group">
          <label for="code">Code aus der App</label>
          <input
            id="code"
            type="text"
            class="input code"
            value={code}
            onInput={(e) => setCode((e.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            autocomplete="one-time-code"
            maxLength={6}
            required
            disabled={phase === 'verifying'}
          />
        </div>

        {error && <div class="error">{error}</div>}

        <button type="submit" class="btn" disabled={phase === 'verifying' || code.length !== 6}>
          {phase === 'verifying' ? <span class="spinner" /> : 'Aktivieren'}
        </button>
      </form>
    </Layout>
  );
};
